import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { debounce } from "lodash"
import React, { ReactNode, useEffect, useState } from "react"
import { Accordion, Card, Form } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import IntlProps from "../../../../state/helpers/IntlProps"
import { ObjectTypeConfig } from "../../../../state/listing/generic-listing"
import {
    ColumnDefinition,
    ColumnDefinitionCategory,
    ColumnDefinitionCategoryHeaderMap
} from "../../../../state/listing/generic-listing/column-definitions"
import { getValueFromObject } from "../../../../utils/object"
import Checkbox from "../../../checkbox/checkbox.component"

interface OwnProps {
    config: ObjectTypeConfig
    title: string
    selected: string[]
    onSelect: (definition: ColumnDefinition) => void
}

type ColumnDefinitionWithContent = {
    content: string
} & ColumnDefinition

const DroppableZoneComponent = (props: OwnProps & IntlProps) => {
    const [search, setSearch] = useState<string>("")
    const [selectorItems, setSelectorItems] = useState<ColumnDefinitionWithContent[]>([])
    const [filteredList, setFilteredList] = useState<ColumnDefinitionWithContent[]>([])
    const [categoryMap, setCategoryMap] = useState<Map<ColumnDefinitionCategory, ColumnDefinitionWithContent[]>>(new Map())
    const [collapsedCategories, setCollapsedCategories] = useState<ColumnDefinitionCategory[]>([])

    useEffect(() => {
        const items = props.config.columnDefinitions
            .filter(columnDef => !columnDef.hidden)
            .map(populateColumnDefinitionContent)
        setSelectorItems(items)
    }, [props.config])

    useEffect(() => {
        setFilteredList(
            selectorItems.filter(definition => definition.content.toLowerCase().includes(search.toLowerCase()))
        )
    }, [selectorItems, search])

    useEffect(() => {
        const map: Map<ColumnDefinitionCategory, ColumnDefinitionWithContent[]> = new Map()
        filteredList.forEach(item => {
            item.categories.forEach(category => {
                map.set(category, [
                    ...(map.get(category) ?? []),
                    item,
                ])
            })
        })
        setCategoryMap(map)
    }, [filteredList])

    const debounceSearchOnChange = debounce((value: string) => {
        setSearch(value)
    }, 500)

    const populateColumnDefinitionContent = (columnDefinition: ColumnDefinition): ColumnDefinitionWithContent => {
        const { id } = columnDefinition    
        return {
            ...columnDefinition,
            content: props.config?.customFieldHeaders?.[id]?.defaultMessage ??
            props.intl.formatMessage(getValueFromObject(props.config?.headerMessages, id)),
        }
    }
    

    const handleToggleCategory = (category: ColumnDefinitionCategory) => {
        const index = collapsedCategories.indexOf(category)

        if (index === -1) {
            setCollapsedCategories([
                ...collapsedCategories,
                category,
            ])
        } else {
            const update = [...collapsedCategories]
            update.splice(index, 1)
            setCollapsedCategories(update)
        }
    }
    
    const getAccordionComponents = (): ReactNode[] => {
        const sections: ReactNode[] = []
        Object.values(ColumnDefinitionCategory)
            .forEach(key => {
                const items = categoryMap.get(key) ?? []

                if (items.length) {
                    sections.push(
                        <Accordion key={key} defaultActiveKey={key}>
                            <Card key={key}>
                                <Accordion.Toggle
                                    as={Card.Header}
                                    eventKey={key}
                                    onClick={() => handleToggleCategory(key)}
                                >
                                    <FormattedMessage {...ColumnDefinitionCategoryHeaderMap[key]} />
                                    <FontAwesomeIcon className={collapsedCategories.includes(key) ? "collapsed" : ""} icon="angle-down" size="lg" />
                                </Accordion.Toggle>
                                <Accordion.Collapse eventKey={key}>
                                    <Card.Body>
                                        {items.map(item => (
                                            <div key={item.id} className="selector-item">
                                                <div className="label">
                                                    <Checkbox
                                                        checked={props.selected.includes(item.id)}
                                                        disabled={item.identifier}
                                                        onChange={() => props.onSelect(item)}
                                                    />
                                                    <span>{item.content}</span>
                                                </div>
                                                {!!item.totalAttributes && (
                                                    <FontAwesomeIcon icon="calculator" />
                                                )}
                                            </div>
                                        ))}
                                    </Card.Body>
                                </Accordion.Collapse>
                            </Card>
                        </Accordion>
                    )
                }
            })
        return sections
    }

    return (
        <div>
            <h5>{props.title}</h5>
            <Form>
                <Form.Group className='mb-0' controlId='formSearch'>
                    <Form.Control
                        type='text'
                        placeholder={props.intl.formatMessage({
                            id: "generic-listing.column-selection-modal.search",
                            defaultMessage: "Search..."
                        })}
                        onChange={e => debounceSearchOnChange(e.target.value)}
                    />
                </Form.Group>
            </Form>
            <div className="column-selector">
                {getAccordionComponents()}
            </div>
        </div>
    )
}

export default injectIntl(DroppableZoneComponent)


// WEBPACK FOOTER //
// ./src/common/listing/generic-listing/header-selection/ColumnSelector.tsx